<template>
  <div>
    <slot/>
  </div>
</template>

<script setup>
import Highcharts from "@/highcharts"
import colors from "@/colors"
import { onMounted } from "vue"
const props = defineProps({
  year1: Number,
  firstSalaryYear: Number,
  lastSalaryYear: Number,
  firstFirmSize: Number,
  allSalaries: Array,
  categories: Array,
  inflationCategories: Array,
  allSalariesInflation1: Array,
  allSalariesInflation2: Array,
  salariesByLawFirmSize1: Array,
  salariesByLawFirmSize2: Array,
})

const chartColors = [
  colors.blue[400],
  colors.orange[400],
  colors.yellow[400],
  colors.teal[400],
  colors.rose[400],
  colors.indigo[400],
  colors.fuchsia[400],
  colors.lime[400],
]

onMounted(() => {
  const {
    categories,
    inflationCategories, allSalaries,
    year1, firstSalaryYear, lastSalaryYear,
    salariesByLawFirmSize1,
    salariesByLawFirmSize2,
    allSalariesInflation1, allSalariesInflation2, firstFirmSize,
  } = props

  const series = allSalaries.map(item => ({
    name: item.name,
    type: "spline",
    color: item.color,
    yAxis: 0,
    marker: { enabled: false, symbol: "circle" },
    data: item.data,
  }))

  new Highcharts.Chart({
    chart: { renderTo: "allSals", zoomType: "xy" },
    title: { text: `Median Salary ${firstSalaryYear}-${lastSalaryYear}` },
    subtitle: { text: "Current Dollars, By Type of Employer" },
    xAxis: {
      categories, tickInterval: 2, labels: { rotation: 0, align: "center" }, crosshair: true,
    },
    yAxis: [
      { // salary
        gridLineWidth: 1,
        title: { text: "", style: { color: "#4572A7" } },
        labels: { formatter() {
                    return `$${this.value / 1000}k`
                  },
                  style: { color: "#4572A7" } },
        opposite: false,
        endOnTick: true,
        tickInterval: 20000,
        min: 0,
        max: 140000,
      },
    ],
    tooltip: {
      shared: true,
      formatter() {
        let s = `<b>${this.x}</b>`
        $.each(this.points, function () {
          s += `<br/><span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${
            this.y}</b>`
        })

        return s
      },
    },
    series,
  })

  const allSalsInflationSeries = []

  allSalariesInflation1.forEach((salary, index) => {
    allSalsInflationSeries.push({
      ...salary,
      type: "spline",
      yAxis: 0,
      marker: { enabled: false, symbol: "circle" },
      color: chartColors[index],
    })

    const inflationSalary = allSalariesInflation2[index]

    allSalsInflationSeries.push({
      ...inflationSalary,
      name: `${inflationSalary.name} (${year1} baseline)`,
      type: "spline",
      yAxis: 0,
      dashStyle: "shortdot",
      linkedTo: ":previous",
      marker: { enabled: false, symbol: "triangle" },
      color: chartColors[index],
    })
  })

  new Highcharts.Chart({
    chart: { renderTo: "allSalsInflation", zoomType: "xy" },
    title: { text: `Median Salary ${year1}-${lastSalaryYear} by Type of Employer` },
    subtitle: { text: `Real Dollars (${year1} $) vs Current Dollars` },
    xAxis: { categories: inflationCategories, tickInterval: 2, labels: { rotation: 0, align: "center" }, crosshair: true },
    yAxis: [
      { // salary
        gridLineWidth: 1,
        title: { text: "", style: { color: "#4572A7" } },
        labels: { formatter() {
                    return `$${this.value / 1000}k`
                  },
                  style: { color: "#4572A7" } },
        opposite: false,
        endOnTick: true,
        min: 0,
      },
    ],
    tooltip: { shared: true },
    series: allSalsInflationSeries,
  })

  const firmSalsInflationSeries = []

  salariesByLawFirmSize1.forEach((salary, index) => {
    firmSalsInflationSeries.push({
      ...salary,
      type: "spline",
      yAxis: 0,
      marker: { enabled: false, symbol: "circle" },
      color: chartColors[index],
    })

    const inflationSalary = salariesByLawFirmSize2[index]

    firmSalsInflationSeries.push({
      ...inflationSalary,
      name: `${inflationSalary.name} (${year1} baseline)`,
      type: "spline",
      yAxis: 0,
      dashStyle: "shortdot",
      linkedTo: ":previous",
      marker: { enabled: false, symbol: "triangle" },
      color: chartColors[index],
    })
  })

  new Highcharts.Chart({
    chart: { renderTo: "firmSalsInflation", zoomType: "xy" },
    title: {
      text: `Median Salary ${year1 < firstFirmSize ? firstFirmSize : year1}-${lastSalaryYear} by Law Firm Size`,
    },
    subtitle: {
      text: `Real Dollars (${year1 < firstFirmSize ? firstFirmSize : year1} $) vs Current Dollars`,
    },
    xAxis: { categories: inflationCategories, tickInterval: 2, labels: { rotation: 0, align: "center" }, crosshair: true },
    yAxis: [
      { // salary
        gridLineWidth: 1,
        title: { text: "", style: { color: "#4572A7" } },
        labels: { formatter() {
                    return `$${this.value / 1000}k`
                  },
                  style: { color: "#4572A7" } },
        opposite: false,
        endOnTick: true,
        min: 0,
      },
    ],
    tooltip: { shared: true },
    series: firmSalsInflationSeries,
  })
})
</script>
